/**
 * EACH WORLD'S SETTING ON THIS DEVICE — measured, not listed. Every surface that draws worlds (the TV receivers,
 * the phone surface, the web + desktop Visualizer, the Apple TV host with its own copy) calibrates each world it
 * shows, once it has settled alone on screen: the highest detail that meets the surface's smoothness bar at
 * NATIVE resolution (renderPolicy.ts — resolution is never a lever). That detail is the world's setting here,
 * applied whenever it returns. A world that misses the bar even at its minimum detail is HEAVY on this device: it
 * leaves every list — the grid, the Director's pool, the playlist's advance (catalogue.loadable) — and a TV names
 * it in its offer to the phone casting to it (receiver/offer.ts). A world on screen keeps it through a heavy
 * verdict near the bar; one CONCLUSIVELY far over it (p95 ≥ FAR_MISS × the bar — farHeavyHere) leaves the screen
 * at once (tvStillPlayable).
 *
 * A WEAK TV starts from a BASIS (devicePriors.ts): what its GPU class is known to draw, and conservative for the
 * rest. The device's own measurement always wins over it, both ways.
 *
 * Why measured: what a world costs depends on the box (GPU, memory, the WebView), and a hand-kept id list of
 * "the light ones" goes stale with every pin bump and every new world.
 *
 * Decided ONCE per device: the settings are remembered (localStorage, wrapped — a box without it calibrates again
 * each run), keyed by surface and by what they were found under — the policy (POLICY_VERSION) and the engine
 * build. A new rule or a new engine (a world may have got cheaper) calibrates again.
 */
import { Engine } from '@syqel/ota-client'
import { farMiss, POLICY_VERSION, SURFACES, type Calibration, type Surface } from './renderPolicy'

/** One world's setting here: the detail it meets the bar at (null = not even at its minimum: heavy), and the
 *  frame rate + 95th-percentile frame time measured there. */
export interface WorldSample { detail: number | null; fps: number; p95Ms: number }

interface Store { policy: number; build: string; worlds: Record<string, WorldSample> }

const KEY_PREFIX = 'syqel.worldSmooth.'
let surface: Surface | null = null
let measured: Record<string, WorldSample> = {}
const listeners = new Set<() => void>()

/**
 * What a device knows BEFORE measuring (devicePriors.ts — a weak tier only; null everywhere else):
 *  - `priors`: verdicts measured on a box of this GPU class (a world's starting point here);
 *  - `unproven(id)`: true = a world neither this device nor its class has measured is treated as heavy (left out,
 *    offered as heavy) until it is measured here — never assumed smooth on a weak GPU.
 */
export interface CostBasis {
  label: string
  priors: Record<string, WorldSample>
  unproven: (id: string) => boolean
}
let basis: CostBasis | null = null

/** Declare (or clear) this device's cost basis — once, as its tier is declared (receiver/weakTier.ts). */
export function setCostBasis(b: CostBasis | null): void {
  basis = b
  if (b) console.log(`[syqel] cost basis: ${b.label}`)
  notify()
}

/** A world's verdict here: this device's own measurement, else its class's prior (undefined = neither). */
function verdictOf(id: string): WorldSample | undefined {
  return measured[id] ?? basis?.priors[id]
}

function judgedUnder(): { policy: number; build: string } {
  return { policy: POLICY_VERSION, build: String(Engine.ENGINE_BUILD ?? '') }
}

/** The surface this page draws on decides the bar and the store; called once, before the first frame. */
export function setCostSurface(s: Surface): void {
  if (surface === s) return
  surface = s
  measured = {}
  try {
    const raw = localStorage.getItem(KEY_PREFIX + s)
    const v = raw ? (JSON.parse(raw) as Partial<Store>) : null
    const now = judgedUnder()
    if (v && v.policy === now.policy && v.build === now.build && v.worlds && typeof v.worlds === 'object') measured = v.worlds
  } catch { /* no storage, or not ours — measure again */ }
  notify()
}

function persist(): void {
  if (!surface) return
  try { localStorage.setItem(KEY_PREFIX + surface, JSON.stringify({ ...judgedUnder(), worlds: measured } satisfies Store)) } catch { /* this run only */ }
}

function notify(): void {
  for (const cb of [...listeners]) { try { cb() } catch { /* a listener must not break the rest */ } }
}

/** The frame rate known for a world here — measured on this device, or on its GPU class (undefined = neither). */
export function measuredFps(id: string): number | undefined {
  const s = verdictOf(id)
  return s && Number.isFinite(s.fps) ? s.fps : undefined
}

/** Unproven here: no verdict for it on this device or its class, and the basis doesn't assume it smooth (a weak
 *  GPU — devicePriors.ts). Heavy until measured; never handed out by a Director's fallback. */
export function unprovenHere(id: string): boolean {
  return !verdictOf(id) && (basis?.unproven(id) ?? false)
}

/** Too heavy for this device: no detail meets the bar at native resolution — measured here, known for its GPU
 *  class, or unproven on a weak GPU. A world with no verdict on a capable device is not heavy. */
export function heavyHere(id: string): boolean {
  const s = verdictOf(id)
  return s ? s.detail === null : unprovenHere(id)
}

/** Heavy AND conclusively far over the bar (p95 ≥ FAR_MISS × the bar, at its minimum detail): not even a world to
 *  hold on screen while it is the current pick — it leaves the screen at once (catalogue.tvStillPlayable). A world
 *  near the bar, or one merely unproven, isn't. */
export function farHeavyHere(id: string): boolean {
  const s = verdictOf(id)
  return !!s && !!surface && farMiss(s, SURFACES[surface])
}

/** Every world this device measured heavy (the phone surface reports them to its shell; a TV's offer). */
export function heavyIds(): string[] {
  return Object.keys(measured).filter(heavyHere)
}

/** This device's setting for a world (undefined = never calibrated here) — its governor starts from it. */
export function calibrationOf(id: string): Calibration | undefined {
  const s = measured[id]
  return s && (s.detail === null || Number.isFinite(s.detail)) ? { detail: s.detail, p95Ms: s.p95Ms, fps: s.fps } : undefined
}

/** Where a world's calibration STARTS on this device when it has none of its own: its GPU class's detail (null =
 *  known heavy there: its minimum), undefined = authored detail. The device still calibrates — the prior is where
 *  the first frame is drawn, so a weak TV shows a known world smooth from the start. */
export function priorDetailOf(id: string): number | null | undefined {
  const p = basis?.priors[id]
  return p ? p.detail : undefined
}

/** Subscribe to verdict changes (catalogue.ts turns them into its offer-change signal). */
export function onHeavyChange(cb: () => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

function record(id: string, sample: WorldSample): void {
  const was = [heavyHere(id), farHeavyHere(id)]
  measured = { ...measured, [id]: sample }
  persist()
  if (heavyHere(id) !== was[0] || farHeavyHere(id) !== was[1]) notify()
}

/**
 * Remember the setting the world's governor found (renderPolicy.ts). Called once per rendered frame with the
 * world on screen and its governor; records a world when its setting is new or changed (a passage that missed
 * the bar re-calibrates it downward).
 */
export function recordSmoothness(worldId: string, gov: { result: Calibration | null }): void {
  const r = gov.result
  if (!surface || !r) return
  const was = measured[worldId]
  if (was && was.detail === r.detail) return
  // the verdict is told BEFORE its effects (the offer to a sender, the lists): a log read alongside them never lags
  console.log(`[syqel] smooth ${worldId}: ${r.fps.toFixed(1)} fps, p95 ${r.p95Ms} ms at native resolution — ${r.detail === null ? `HEAVY on this device (misses the bar p95 ≤ ${SURFACES[surface].bar.maxP95Ms} ms even at its minimum detail): leaves the lists` : `detail ${r.detail.toFixed(2)}`}`)
  record(worldId, { detail: r.detail, fps: r.fps, p95Ms: r.p95Ms })
}
